import React, { FC, useState, useCallback, useRef, useEffect } from "react";
import {
  Trash2, Wand2, Download, ChevronDown, Cpu, Play, Pause,
  Type, Lightbulb, ArrowRight, BookOpen, Tv, Megaphone, Radio, Mic,
  AudioLines, Search, Check,
} from "lucide-react";
import { Popconfirm } from "antd";
import { useAlert } from "../../hooks/useAlert";
import { AlertMessage } from "../../components/common/AlertMessage";
import { resolvePublicVoicePreviewFullUrl } from "../../service/voicePublic";
import { fetchPublicVoiceListMulti } from "../voicePublic/publicVoiceMultiFetch";
import { fetchMineVoiceListMulti } from "../voicePublic/mineVoiceMultiFetch";
import { VIDEO_CDN_PREFIX } from "../../config/video";
import { ttsGenerateApi, ttsHistoryPageApi, ttsHistoryDeleteApi, ttsTemplateApi } from "../../service/textToAudio";
import type { TtsHistoryItem, TtsModel, TtsTemplateItem } from "../../service/textToAudio";
import { sanitizeVideoFileBaseName } from "../../utils/batchVideoDownload";
import { triggerPointsRefresh } from "../../service/points";
import { triggerMembershipRefresh } from "../../service/membership";
import "./TextToAudio.scss";

const MAX_CHARS = 3000;
const HISTORY_PAGE_SIZE = 20;
const DEFAULT_TTS_MODEL: TtsModel = "Lychee Voice_TTS_PRO";
const TEMPLATE_PLAY_ID_OFFSET = 100000;

/** 模型下拉的可选项（value 是后端 model 值；label 为界面展示文案） */
const TTS_MODEL_OPTIONS: { value: TtsModel; label: string; tag?: string }[] = [
  { value: "Lychee Voice_TTS_PRO", label: "Lychee Voice-TTS-Pro", tag: "推荐" },
  { value: "MIMO_TTS", label: "MIMO TTS" },
];

const TTS_TEMPLATE_IMAGE_FALLBACK: Record<string, string> = {
  有声读物: "/assets/audio-templates/audiobook-night-train.png",
  配音视频: "/assets/audio-templates/video-dubbing-robot.png",
  短视频口播: "/assets/audio-templates/short-video-creator.png",
  通知播报: "/assets/audio-templates/announcement-coast-train.png",
};

const TTS_FALLBACK_TEMPLATES: TtsTemplateItem[] = [
  { id: 1, title: "哦~是关中王来了", description: null, script: "哦~是关中王来了", coverImageUrl: null, sampleAudioUrl: null,
    voiceId: null },
  { id: 2, title: "家人们谁懂啊", description: null, script: "家人们谁懂啊", coverImageUrl: null, sampleAudioUrl: null,
    voiceId: null },
  { id: 3, title: "早知他来，我就不来了", description: null, script: "早知他来，我就不来了", coverImageUrl: null, sampleAudioUrl: null,
    voiceId: null },
  {
    id: 4,
    title: "有声读物",
    description: "有声读物",
    script: "凌晨两点十七分，最后一班列车穿过雨幕。林野合上旧书，却发现书页间夹着一张写给三十年后车票。",
    coverImageUrl: null,
    sampleAudioUrl: null,
    voiceId: null,
  },
  {
    id: 5,
    title: "配音视频",
    description: "配音视频",
    script: "录音倒计时开始！三、二、一——等等，我的情绪模块还没加载。好吧，现在请听一位机器人如何认真地讲冷笑话。",
    coverImageUrl: null,
    sampleAudioUrl: null,
    voiceId: null,
  },
  {
    id: 6,
    title: "短视频口播",
    description: "短视频口播",
    script: "别急着划走，三十秒教你把普通柠檬水调出夏日感：冰块加到七分满，再放一小撮海盐，清爽度直接翻倍。",
    coverImageUrl: null,
    sampleAudioUrl: null,
    voiceId: null,
  },
  {
    id: 7,
    title: "通知播报",
    description: "通知播报",
    script: "各位旅客请注意，受近海浓雾影响，蓝湾线今日十点前调整为二十分钟一班。请留意站台指引，合理安排出行。",
    coverImageUrl: null,
    sampleAudioUrl: null,
    voiceId: null,
  },
];

type TtsWorkTab = "templates" | "works";
function getTemplatePlaybackId(id: number): number {
  return -(TEMPLATE_PLAY_ID_OFFSET + id);
}

function resolveTtsAssetUrl(rawUrl?: string | null): string | undefined {
  if (!rawUrl) return undefined;
  if (rawUrl.startsWith("http://")) return `https://${rawUrl.slice("http://".length)}`;
  if (rawUrl.startsWith("https://")) return rawUrl;
  const path = rawUrl.startsWith("/") ? rawUrl : `/${rawUrl}`;
  return `${VIDEO_CDN_PREFIX}${path}`;
}

function getTemplateIcon(title: string) {
  if (title.includes("读物")) return BookOpen;
  if (title.includes("视频")) return Tv;
  if (title.includes("口播")) return Megaphone;
  if (title.includes("通知")) return Radio;
  return BookOpen;
}

function resolveTemplateCover(template: TtsTemplateItem): string | undefined {
  return resolveTtsAssetUrl(template.coverImageUrl) || TTS_TEMPLATE_IMAGE_FALLBACK[template.title];
}
type VoiceSourceTab = "public" | "mine";

interface VoiceOption {
  id: number;
  source: VoiceSourceTab;
  name: string;
  description?: string;
  genderText: string;
  previewUrl: string | null;
}

export const TextToAudio: FC = () => {
  const { alertConfig, showSuccess, showError, hideAlert } = useAlert();
  const [inputText, setInputText] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const generateStartTimeRef = useRef<number | null>(null);
  const [audioSrc, setAudioSrc] = useState<string | null>(null);
  const [currentAudioVoiceName, setCurrentAudioVoiceName] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [history, setHistory] = useState<TtsHistoryItem[]>([]);
  const [deletingHistoryIds, setDeletingHistoryIds] = useState<number[]>([]);
  const [templates, setTemplates] = useState<TtsTemplateItem[]>(TTS_FALLBACK_TEMPLATES);
  const [voiceTab, setVoiceTab] = useState<VoiceSourceTab>("public");
  const [publicVoices, setPublicVoices] = useState<VoiceOption[]>([]);
  const [mineVoices, setMineVoices] = useState<VoiceOption[]>([]);
  const [selectedVoiceId, setSelectedVoiceId] = useState<number | null>(null);
  const [selectedVoiceSource, setSelectedVoiceSource] = useState<VoiceSourceTab>("public");
  const [playingHistoryId, setPlayingHistoryId] = useState<number | null>(null);
  const voices = voiceTab === "public" ? publicVoices : mineVoices;
  const selectedVoiceList = selectedVoiceSource === "public" ? publicVoices : mineVoices;
  const selectedVoice = selectedVoiceList.find(v => v.id === selectedVoiceId);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const voiceBtnRef = useRef<HTMLButtonElement>(null);
  const modelBtnRef = useRef<HTMLButtonElement>(null);
  const [voicePopoverOpen, setVoicePopoverOpen] = useState(false);
  const [voicePopoverPos, setVoicePopoverPos] = useState({ top: 0, left: 0 });
  const [modelPopoverOpen, setModelPopoverOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState<TtsModel>(DEFAULT_TTS_MODEL);
  const [previewVoiceKey, setPreviewVoiceKey] = useState<string | null>(null);
  const [workTab, setWorkTab] = useState<TtsWorkTab>("templates");
  const [historySearch, setHistorySearch] = useState("");
  const [templatePlayingId, setTemplatePlayingId] = useState<number | null>(null);
  const [templatePlayProgress, setTemplatePlayProgress] = useState(0);
  const [templateDurations, setTemplateDurations] = useState<Record<number, number>>({});
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);
  const templateAudioRef = useRef<HTMLAudioElement | null>(null);

  const stopTemplatePlayback = useCallback(() => {
    const audio = templateAudioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    setTemplatePlayingId(null);
    setTemplatePlayProgress(0);
  }, []);

  const charCount = inputText.length;
  const selectedGenderLabel = selectedVoice?.genderText
    ? selectedVoice.genderText.includes("女") || selectedVoice.genderText.includes("濂") ? "女声" : "男声"
    : "音色";
  const filteredHistory = history.filter((item) => (`${item.text || ""} ${item.voiceName || ""}`).toLowerCase().includes(historySearch.trim().toLowerCase()));
  const quickPromptTemplates = templates.slice(0, 3);
  const templateCards = templates.slice(3);

  const mapVoiceOption = (item: VoicePublic.VoiceToneItem, source: VoiceSourceTab): VoiceOption => ({
    id: item.id,
    source,
    name: item.voiceName,
    description: item.description || "",
    genderText: item.genderTextValue || "",
    previewUrl: resolvePublicVoicePreviewFullUrl(item, VIDEO_CDN_PREFIX),
  });

  // 加载公共/我的音色列表
  useEffect(() => {
    const fn = async () => {
      try {
        const [publicList, mineList] = await Promise.all([
          fetchPublicVoiceListMulti("", [], [], [], []),
          fetchMineVoiceListMulti("", [], [], [], []),
        ]);
        const nextPublic = publicList.map((item) => mapVoiceOption(item, "public"));
        const nextMine = mineList.map((item) => mapVoiceOption(item, "mine"));
        setPublicVoices(nextPublic);
        setMineVoices(nextMine);
        const initial = nextPublic[0] ?? nextMine[0];
        if (initial) {
          setVoiceTab(initial.source);
          setSelectedVoiceSource(initial.source);
          setSelectedVoiceId(initial.id);
        }
      } catch {}
    };
    void fn();
  }, []);

  // 加载文本转语音模板
  useEffect(() => {
    let alive = true;
    ttsTemplateApi().then((res) => {
      if (!alive) return;
      if (res.code === 0 && Array.isArray(res.data) && res.data.length > 0) {
        setTemplates(res.data);
      }
    }).catch(() => {});
    return () => { alive = false; };
  }, []);
  // 加载历史记录
  useEffect(() => {
    const fn = async () => {
      try {
        const res = await ttsHistoryPageApi({ pageNo: 1, pageSize: HISTORY_PAGE_SIZE });
        if (res.code === 0 && res.data) setHistory(res.data.list);
      } catch {}
    };
    void fn();
  }, []);


  const syncTemplateVoice = useCallback((template: TtsTemplateItem) => {
    const templateVoiceId = Number(template.voiceId);
    if (!Number.isFinite(templateVoiceId) || templateVoiceId <= 0) return;

    const publicVoice = publicVoices.find((voice) => voice.id === templateVoiceId);
    if (publicVoice) {
      setVoiceTab("public");
      setSelectedVoiceSource("public");
      setSelectedVoiceId(publicVoice.id);
      return;
    }

    const mineVoice = mineVoices.find((voice) => voice.id === templateVoiceId);
    if (mineVoice) {
      setVoiceTab("mine");
      setSelectedVoiceSource("mine");
      setSelectedVoiceId(mineVoice.id);
    }
  }, [publicVoices, mineVoices]);

  const applyTemplate = useCallback((template: TtsTemplateItem) => {
    setInputText(template.script);
    syncTemplateVoice(template);
  }, [syncTemplateVoice]);
  // 播放/暂停
  const togglePlay = useCallback(() => {
    if (!audioRef.current) return;
    stopTemplatePlayback();
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      setPlayingHistoryId(null);
    } else {
      audioRef.current.play().catch(() => {});
      setIsPlaying(true);
    }
  }, [isPlaying, stopTemplatePlayback]);

  // 进度更新
  const handleTimeUpdate = useCallback(() => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  }, []);

  const handleEnded = useCallback(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setPlayingHistoryId(null);
  }, []);

  // 进度条点击跳转
  const seekAudio = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || !progressRef.current) return;
    const rect = progressRef.current.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    audioRef.current.currentTime = ratio * audioRef.current.duration;
  }, []);

  // 下载音频：先转 Blob 再保存，避免浏览器按 CDN URL 文件名命名。
  const downloadAudioFromUrl = useCallback(async (sourceUrl: string, voiceLabel?: string) => {
    const now = new Date();
    const timestamp = [
      now.getFullYear(),
      String(now.getMonth() + 1).padStart(2, "0"),
      String(now.getDate()).padStart(2, "0"),
      String(now.getHours()).padStart(2, "0"),
      String(now.getMinutes()).padStart(2, "0"),
      String(now.getSeconds()).padStart(2, "0"),
    ].join("");
    const voiceName = sanitizeVideoFileBaseName(voiceLabel || selectedVoice?.name || "音色");
    const downloadName = `tts_${timestamp}_${voiceName}.mp3`;

    try {
      const response = await fetch(sourceUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = downloadName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("下载音频失败:", error);
      showError("下载失败，请稍后重试");
    }
  }, [selectedVoice?.name, showError]);

  const downloadAudio = useCallback(async () => {
    if (!audioSrc) return;
    await downloadAudioFromUrl(audioSrc, currentAudioVoiceName);
  }, [audioSrc, currentAudioVoiceName, downloadAudioFromUrl]);

  // 清空
  const clearText = useCallback(() => {
    setInputText("");
  }, []);

  // 格式化时间
  const formatTime = (sec: number) => {
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m < 10 ? "0" : ""}${m}:${s < 10 ? "0" : ""}${s}`;
  };

  const formatDuration = (ms: number) => {
    const sec = Math.floor(ms / 1000);
    return `${Math.floor(sec / 60)}:${(sec % 60).toString().padStart(2, "0")}`;
  };

  const toggleHistoryPlay = useCallback((item: TtsHistoryItem) => {
    if (!item.audioUrl) return;
    stopTemplatePlayback();

    if (playingHistoryId === item.id && isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
      return;
    }

    if (audioSrc === item.audioUrl && audioRef.current) {
      const isUserPausedResume = playingHistoryId === item.id;
      if (!isUserPausedResume) {
        audioRef.current.currentTime = 0;
        setCurrentTime(0);
      }
      setPlayingHistoryId(item.id);
      setCurrentAudioVoiceName(item.voiceName || "");
      audioRef.current.play().catch(() => {});
      setIsPlaying(true);
      return;
    }

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setPlayingHistoryId(item.id);
    setAudioSrc(item.audioUrl);
    setCurrentAudioVoiceName(item.voiceName || "");
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(true);
  }, [audioSrc, isPlaying, playingHistoryId, stopTemplatePlayback]);
  // 删除我的作品
  const handleDeleteHistory = useCallback(async (item: TtsHistoryItem) => {
    if (!item.id || deletingHistoryIds.includes(item.id)) return;
    setDeletingHistoryIds((prev) => [...prev, item.id]);
    try {
      const res = await ttsHistoryDeleteApi(item.id);
      if (res.code === 0) {
        setHistory((prev) => prev.filter((historyItem) => historyItem.id !== item.id));
        if (playingHistoryId === item.id && audioRef.current) {
          audioRef.current.pause();
          setIsPlaying(false);
          setPlayingHistoryId(null);
          setCurrentTime(0);
          setDuration(0);
        }
      } else {
        showError(res.msg || "删除失败，请稍后重试");
      }
    } catch (error) {
      console.error("删除文本转语音作品失败:", error);
      showError("删除失败，请稍后重试");
    } finally {
      setDeletingHistoryIds((prev) => prev.filter((historyId) => historyId !== item.id));
    }
  }, [deletingHistoryIds, playingHistoryId, showError]);

  // 生成语音
  const generateSpeech = useCallback(async () => {
    if (!inputText.trim() || !selectedVoiceId) return;
    stopTemplatePlayback();
    setIsGenerating(true);
    setAudioSrc(null);
    setCurrentAudioVoiceName("");
    setCurrentTime(0);
    setDuration(0);
    generateStartTimeRef.current = Date.now();
    window.setTimeout(() => {
      if (generateStartTimeRef.current && Date.now() - generateStartTimeRef.current >= 2000) {
        triggerMembershipRefresh();
      }
    }, 2000);
    try {
      const res = await ttsGenerateApi({
        voiceId: selectedVoiceId,
        voiceSourceType: selectedVoiceSource === "public" ? "PUBLIC_VOICE_TONE" : "USER_VOICE_TONE",
        text: inputText.trim(),
        model: selectedModel,
      });
      if (res.code === 0 && res.data?.audioUrl) {
        showSuccess("语音生成成功");
        triggerPointsRefresh();
        if (!generateStartTimeRef.current || Date.now() - generateStartTimeRef.current >= 2000) {
          triggerMembershipRefresh();
        }
        setHistorySearch("");
        setWorkTab("works");

        const fres = await ttsHistoryPageApi({ pageNo: 1, pageSize: HISTORY_PAGE_SIZE });
        const nextHistory = fres.code === 0 && fres.data ? fres.data.list : [];
        if (nextHistory.length > 0) setHistory(nextHistory);

        const generatedItem = nextHistory.find((item) => item.id === res.data.recordId)
          ?? nextHistory.find((item) => item.audioUrl === res.data.audioUrl);
        const nextAudioUrl = generatedItem?.audioUrl || res.data.audioUrl;
        const nextPlayingId = generatedItem?.id || res.data.recordId;

        setPlayingHistoryId(nextPlayingId);
        setAudioSrc(nextAudioUrl);
        setCurrentAudioVoiceName(generatedItem?.voiceName || selectedVoice?.name || "");
        setCurrentTime(0);
        setDuration(0);
        setIsPlaying(true);
      } else if (res.msg) {
        showError(res.msg);
      }
    } catch (error) {
      console.error("文本转语音生成失败:", error);
    } finally {
      setIsGenerating(false);
    }
  }, [inputText, selectedVoiceId, selectedVoiceSource, selectedModel, selectedVoice?.name, stopTemplatePlayback]);

  // 音频源变化时自动播放
  useEffect(() => {
    if (audioSrc && isPlaying && audioRef.current) {
      audioRef.current.play().catch(() => {});
    }
  }, [audioSrc]);

  // 模型下拉：点击外部关闭（与 AI 生图一致）
  useEffect(() => {
    if (!modelPopoverOpen) return undefined;
    const close = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && target.closest('.select-wrap[data-key="model"]')) return;
      setModelPopoverOpen(false);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [modelPopoverOpen]);


  useEffect(() => {
    const audio = new Audio();
    audio.preload = "auto";
    const onTimeupdate = () => {
      if (audio.duration) setTemplatePlayProgress((audio.currentTime / audio.duration) * 100);
    };
    const onLoadedMetadata = () => {
      const rawPlayingId = Number(audio.dataset.playingId);
      if (Number.isFinite(rawPlayingId) && rawPlayingId < 0 && audio.duration) {
        const templateId = Math.abs(rawPlayingId) - TEMPLATE_PLAY_ID_OFFSET;
        setTemplateDurations((prev) => ({ ...prev, [templateId]: audio.duration }));
      }
    };
    const onEnded = () => { setTemplatePlayingId(null); setTemplatePlayProgress(0); };
    const onError = () => { setTemplatePlayingId(null); setTemplatePlayProgress(0); };
    audio.addEventListener("timeupdate", onTimeupdate);
    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("error", onError);
    templateAudioRef.current = audio;
    return () => {
      audio.removeEventListener("timeupdate", onTimeupdate);
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("error", onError);
      audio.pause();
      audio.src = "";
      templateAudioRef.current = null;
    };
  }, []);

  const startTemplatePlayback = useCallback((id: number, audioUrl?: string) => {
    if (!audioUrl) return;
    audioRef.current?.pause();
    setIsPlaying(false);
    setPlayingHistoryId(null);
    setCurrentTime(0);
    const audio = templateAudioRef.current;
    if (!audio) return;
    audio.dataset.playingId = String(id);
    audio.src = audioUrl;
    audio.currentTime = 0;
    audio.play().catch(() => {});
    setTemplatePlayingId(id);
    setTemplatePlayProgress(0);
  }, []);

  const toggleTemplatePlay = useCallback((id: number, audioUrl?: string) => {
    if (templatePlayingId === id && templateAudioRef.current && !templateAudioRef.current.paused) {
      stopTemplatePlayback();
    } else {
      startTemplatePlayback(id, audioUrl);
    }
  }, [templatePlayingId, stopTemplatePlayback, startTemplatePlayback]);

  const stopVoicePreviews = useCallback(() => {
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
    }
    setPreviewVoiceKey(null);
  }, []);

  return (
    <div className="tts-page">
      <AlertMessage config={alertConfig} onClose={hideAlert} />
      <div className="tts-studio-shell">
        <section className="tts-studio-hero">
          <div className="tts-section-kicker">Text To Speech</div>
          <h1>文本转语音</h1>
          <p>输入一段文案，选择合适音色和推理模型，快速生成可试听、可下载的高质量语音。</p>
        </section>

        <section className="tts-primary-panel">
          <div className="tts-composer-card tts-card-hover">
            <textarea
              className="tts-textarea compact"
              placeholder="输入文字并选择合适的音色，我们帮你自然地读出来"
              maxLength={MAX_CHARS}
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
            />
            <div className="tts-composer-footer">
              <div className="tts-toolbar tts-toolbar--inline">
                <button
                  ref={voiceBtnRef}
                  type="button"
                  className={`tts-inline-select${voicePopoverOpen ? " active" : ""}`}
                  onClick={() => {
                    if (voiceBtnRef.current) {
                      const r = voiceBtnRef.current.getBoundingClientRect();
                      setVoicePopoverPos({ top: r.bottom + 4, left: r.left });
                    }
                    setVoicePopoverOpen(v => !v);
                  }}
                >
                  <Mic size={12} style={{ color: "#64748b", flexShrink: 0 }} />
                  <span className="tts-sel-value">{selectedVoice?.name || "选择音色"}</span>
                  <span className="tts-sel-label">· {selectedGenderLabel}</span>
                  <ChevronDown size={12} className="tts-sel-chevron" />
                </button>
                {voicePopoverOpen && (
                  <>
                    <div className="tts-popover-overlay" onClick={() => { setVoicePopoverOpen(false); stopVoicePreviews(); }} />
                    <div className="tts-popover show" style={{ top: voicePopoverPos.top, left: voicePopoverPos.left }}>
                      <div className="tts-popover-tabs">
                        {(["public", "mine"] as const).map((tab) => {
                          const list = tab === "public" ? publicVoices : mineVoices;
                          return (
                            <button
                              key={tab}
                              type="button"
                              className={`tts-popover-tab${voiceTab === tab ? " active" : ""}`}
                              onClick={() => {
                                setVoiceTab(tab);
                                stopVoicePreviews();
                                const first = list[0];
                                if (first) {
                                  setSelectedVoiceSource(first.source);
                                  setSelectedVoiceId(first.id);
                                } else {
                                  setSelectedVoiceSource(tab);
                                  setSelectedVoiceId(null);
                                }
                              }}
                            >
                              {tab === "public" ? "公共音色" : "我的音色"}
                            </button>
                          );
                        })}
                      </div>
                      <div className="tts-popover-list">
                        {voices.length === 0 ? (
                          <div className="tts-popover-empty">
                            暂无可用{voiceTab === "public" ? "公共音色" : "我的音色"}
                          </div>
                        ) : voices.map(v => {
                          const isSelected = v.id === selectedVoiceId && v.source === selectedVoiceSource;
                          const genderTag = v.genderText.includes("女") || v.genderText.includes("濂") ? "女" : v.genderText.includes("男") || v.genderText.includes("鐢") ? "男" : "";
                          const previewKey = `${v.source}-${v.id}`;
                          return (
                            <div
                              key={previewKey}
                              className={`tts-popover-option${isSelected ? " selected" : ""}`}
                            >
                              <button
                                type="button"
                                className="tts-popover-info"
                                onClick={() => { setSelectedVoiceId(v.id); setSelectedVoiceSource(v.source); setVoicePopoverOpen(false); stopVoicePreviews(); }}
                              >
                                <span className="tts-popover-name-row">
                                  {genderTag && <span className={`tts-popover-gender-dot ${genderTag === "男" ? "blue" : "pink"}`} />}
                                  <span className="tts-popover-name">{v.name}</span>
                                </span>
                                {v.description?.trim() ? (
                                  <span className="tts-popover-voice-description" title={v.description}>
                                    {v.description}
                                  </span>
                                ) : null}
                              </button>
                              {isSelected && <div className="tts-popover-check"><Check size={16} /></div>}
                              <button
                                type="button"
                                className="tts-popover-play"
                                onClick={e => {
                                  e.stopPropagation();
                                  if (previewVoiceKey === previewKey && previewAudioRef.current) {
                                    previewAudioRef.current.pause();
                                    previewAudioRef.current = null;
                                    setPreviewVoiceKey(null);
                                  } else if (v.previewUrl) {
                                    if (previewAudioRef.current) { previewAudioRef.current.pause(); previewAudioRef.current = null; }
                                    const a = new Audio(v.previewUrl);
                                    a.onended = () => { setPreviewVoiceKey(null); previewAudioRef.current = null; };
                                    a.play().catch(() => {});
                                    previewAudioRef.current = a;
                                    setPreviewVoiceKey(previewKey);
                                  }
                                }}
                                title="试听"
                              >
                                {previewVoiceKey === previewKey ? <Pause size={15} /> : <Play size={15} />}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}
                <div className="select-wrap" data-key="model">
                <button
                  ref={modelBtnRef}
                  type="button"
                  className="select-button model-button"
                  aria-expanded={modelPopoverOpen}
                  title={selectedModel}
                  onClick={(event) => {
                    event.stopPropagation();
                    setModelPopoverOpen((v) => !v);
                  }}
                >
                  <Cpu size={14} color="#64748b" />
                  <span>
                    {TTS_MODEL_OPTIONS.find((o) => o.value === selectedModel)
                      ?.label || selectedModel}
                  </span>
                  <ChevronDown size={12} />
                </button>
                <div className={`select-menu${modelPopoverOpen ? " open" : ""}`}>
                  {TTS_MODEL_OPTIONS.map((opt) => {
                    const isSelected = opt.value === selectedModel;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        className={isSelected ? "selected" : ""}
                        data-value={opt.value}
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedModel(opt.value);
                          setModelPopoverOpen(false);
                        }}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              </div>
              <div className="tts-composer-actions">
                <button type="button" className="tts-clear-btn" onClick={clearText}>
                  <Trash2 size={12} />清空
                </button>
                <button
                  type="button"
                  className="tts-arrow-generate"
                  disabled={isGenerating || !inputText.trim() || !selectedVoiceId}
                  onClick={generateSpeech}
                  title="生成语音"
                  aria-busy={isGenerating}
                >
                  {isGenerating ? <Wand2 className="tts-spin" size={20} /> : <ArrowRight size={20} />}
                </button>
              </div>
            </div>
          </div>
          {audioSrc && (
            <audio
              ref={audioRef}
              src={audioSrc}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onEnded={handleEnded}
              style={{ display: "none" }}
            />
          )}
        </section>

        <div className="tts-quick-prompts">
          {quickPromptTemplates.map((prompt) => (
            <button key={prompt.id} type="button" className="tts-quick-prompt" onClick={() => applyTemplate(prompt)}>
              <Lightbulb size={17} />
              <span>{prompt.title}</span>
            </button>
          ))}
        </div>

        <section className="tts-work-section">
          <div className="tts-work-tabs">
            <button type="button" className={`tts-work-tab${workTab === "templates" ? " active" : ""}`} onClick={() => setWorkTab("templates")}>模板</button>
            <button type="button" className={`tts-work-tab${workTab === "works" ? " active" : ""}`} onClick={() => setWorkTab("works")}>我的作品</button>
          </div>

          {workTab === "templates" ? (
            <div className="tts-template-grid">
              {templateCards.map((template) => {
                const Icon = getTemplateIcon(template.title);
                const templatePlayId = getTemplatePlaybackId(template.id);
                const sampleAudioUrl = resolveTtsAssetUrl(template.sampleAudioUrl);
                const isTemplatePlaying = templatePlayingId === templatePlayId;
                const templateDuration = templateDurations[template.id] ?? 0;
                const templateCover = resolveTemplateCover(template);
                return (
                  <button key={template.id} type="button" className="tts-template-card" onClick={() => applyTemplate(template)}>
                    <span className="tts-template-art" style={templateCover ? { backgroundImage: `url(${templateCover})` } : undefined} />
                    <span className="tts-template-body">
                      <span className="tts-template-text">{template.script}</span>
                      <span className="tts-template-meta">
                        <span className="tts-template-tag"><Icon size={16} />{template.description || template.title}</span>
                        <span className="tts-template-audio" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            className="tts-template-play"
                            data-state={isTemplatePlaying ? "pause" : "play"}
                            title={isTemplatePlaying ? "暂停" : "播放"}
                            disabled={!sampleAudioUrl}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleTemplatePlay(templatePlayId, sampleAudioUrl);
                            }}
                          >
                            {isTemplatePlaying ? <Pause size={16} /> : <Play size={16} fill="currentColor" />}
                          </button>
                        </span>
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="tts-work-panel">
              <div className="tts-works-toolbar">
                <label className="tts-works-search">
                  <Search size={17} />
                  <input value={historySearch} onChange={(e) => setHistorySearch(e.target.value)} placeholder="搜索你的项目" />
                </label>
                <span className="tts-history-count">{filteredHistory.length} 条</span>
              </div>
              {filteredHistory.length === 0 ? (
                <div className="tts-empty-works">
                  <AudioLines size={26} />
                  <span>{history.length === 0 ? "暂无生成记录" : "没有匹配的作品"}</span>
                </div>
              ) : (
                <div className="tts-history-grid">
                  {filteredHistory.map((item) => {
                    const isPlayingItem = playingHistoryId === item.id;
                    const isDeletingHistory = deletingHistoryIds.includes(item.id);
                    return (
                      <article
                        key={item.id}
                        className={`tts-history-card${isPlayingItem && isPlaying ? " playing" : ""}`}
                      >
                        <AudioLines className="tts-history-wave" />
                        <h3 className="tts-history-title">{item.text}</h3>
                        <div className="tts-history-meta">
                          <div className="tts-history-meta-left">
                            <span className="tts-history-voice-tag">{item.voiceName || "音色"}</span>
                            <span className="tts-history-duration">{formatDuration(item.durationMs)}</span>
                          </div>
                          <div className="tts-history-actions">
                            {item.status === 2 && <span className="tts-history-failed">失败</span>}
                            <button
                              type="button"
                              className="tts-history-download"
                              disabled={!item.audioUrl}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (item.audioUrl) void downloadAudioFromUrl(item.audioUrl, item.voiceName);
                              }}
                              title="下载"
                            >
                              <Download size={14} />
                            </button>
                            <Popconfirm
                              title="确定要删除这个作品吗？"
                              okText="确定"
                              cancelText="取消"
                              onPopupClick={(e) => {
                                e.stopPropagation();
                              }}
                              onConfirm={() => {
                                void handleDeleteHistory(item);
                              }}
                            >
                              <button
                                type="button"
                                className="tts-history-delete"
                                title={isDeletingHistory ? "删除中" : "删除"}
                                disabled={isDeletingHistory}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                              >
                                <Trash2 size={14} />
                              </button>
                            </Popconfirm>
                            <button
                              type="button"
                              className="tts-history-play"
                              data-state={isPlayingItem && isPlaying ? "pause" : "play"}
                              disabled={!item.audioUrl}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleHistoryPlay(item);
                              }}
                              title={isPlayingItem && isPlaying ? "暂停" : "播放"}
                            >
                              {isPlayingItem && isPlaying ? <Pause size={14} /> : <Play size={14} fill="currentColor" />}
                            </button>
                          </div>
                        </div>
                        <div className="tts-history-card-player">
                          {/* 隐藏进度条和时间 */}
                          {false && (
                            <div className="tts-history-progress">
                              <span
                                className="tts-history-progress-fill"
                                style={{ width: isPlayingItem && duration > 0 ? `${(currentTime / duration) * 100}%` : "0%" }}
                              />
                            </div>
                          )}
                          {false && (
                            <span className="tts-history-time">
                              {isPlayingItem && isPlaying ? formatTime(currentTime) : formatDuration(item.durationMs)}
                            </span>
                          )}
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};
